// src/components/chat/Bookmark.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Bookmark = ({ isOpen, onClose, onSave, initialContent, initialPrompt }) => {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [contentStart, setContentStart] = useState('');
  const [contentEnd, setContentEnd] = useState('');
  const [availability, setAvailability] = useState({
    private: true,
    company: false,
    community: false
  });
  const [displayType, setDisplayType] = useState('appBar'); // Changed default to 'appBar'
  const [selectedIcon, setSelectedIcon] = useState('bookmark');
  const [iconBgColor, setIconBgColor] = useState('bg-sky-500');
  const [favorite, setFavorite] = useState(true); // Added favorite state, defaults to true

  const USER_HIGHLIGHTED_TEXT = "text that the user has highlighted gos here";

  // Set initial values when the modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialContent) {
        setName(initialContent.name || '');
        setAvailability(initialContent.availability || { private: true, company: false, community: false });

        let type = 'appBar'; // Changed default to 'appBar'
        if (initialContent.type) {
          type = initialContent.type;
        } else if (initialContent.favorite) {
          type = 'appBar';
        } else if (initialContent.inlineConversation) {
          type = 'inline';
        }
        setDisplayType(type);

        setSelectedIcon(initialContent.icon?.name || 'bookmark');
        setIconBgColor(initialContent.icon?.bgColor || 'bg-sky-500');
        setFavorite(initialContent.favorite !== undefined ? initialContent.favorite : true); // Set favorite from initialContent or default to true

        if (type === 'inline') {
          const messageContent = initialContent.content || '';
          const parts = messageContent.split(USER_HIGHLIGHTED_TEXT);
          if (parts.length >= 2) {
            setContentStart(parts[0].trim());
            setContentEnd(parts.slice(1).join(USER_HIGHLIGHTED_TEXT).trim());
          } else {
            setContentStart(messageContent);
            setContentEnd('');
          }
          setContent('');
        } else { // appBar
          setContent(initialContent.content || '');
          setContentStart('');
          setContentEnd('');
        }
      } else {
        // Creating new bookmark
        setName('');
        const defaultNewType = 'appBar'; // Changed default to 'appBar'
        setDisplayType(defaultNewType);

        if (defaultNewType === 'inline') {
          setContentStart(initialPrompt || '');
          setContentEnd('');
          setContent('');
        } else { // appBar
          setContent(initialPrompt || '');
          setContentStart('');
          setContentEnd('');
        }

        setAvailability({ private: true, company: false, community: false });
        setSelectedIcon('bookmark');
        setIconBgColor('bg-sky-500');
        setFavorite(true); // Default new bookmarks to favorite
      }
    }
  }, [isOpen, initialContent, initialPrompt, USER_HIGHLIGHTED_TEXT]);

  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setName('');
        setContent('');
        setContentStart('');
        setContentEnd('');
        setAvailability({ private: true, company: false, community: false });
        setDisplayType('appBar'); // Changed default to 'appBar'
        setSelectedIcon('bookmark');
        setIconBgColor('bg-sky-500');
        setFavorite(true); // Reset to default favorite
      }, 300);
    }
  }, [isOpen]);

  const handleDisplayTypeChange = (newType) => {
    const oldType = displayType;
    if (newType === oldType) return;

    if (newType === 'inline') {
      if (oldType === 'appBar') {
        setContentStart(content);
        setContentEnd('');
        setContent('');
      }
    } else if (newType === 'appBar') {
      if (oldType === 'inline') {
        setContent(`${contentStart} ${USER_HIGHLIGHTED_TEXT} ${contentEnd}`);
        setContentStart('');
        setContentEnd('');
      }
    }
    setDisplayType(newType);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let finalContent;
    if (displayType === 'inline') {
      finalContent = `${contentStart} ${USER_HIGHLIGHTED_TEXT} ${contentEnd}`;
    } else {
      finalContent = content;
    }

    onSave({
      name,
      content: finalContent,
      availability,
      type: displayType,
      icon: {
        name: selectedIcon,
        bgColor: iconBgColor
      },
      favorite
    });
    onClose();
  };

  const handleAvailabilityChange = (type) => {
    if (type === 'private') {
      setAvailability({
        private: true,
        company: false,
        community: false
      });
    } else {
      setAvailability({
        ...availability,
        [type]: !availability[type],
        private: false
      });
    }
  };

  // Available icons
  const icons = [
    { id: 'bookmark', name: 'Bookmark' },
    { id: 'star', name: 'Star' },
    { id: 'heart', name: 'Heart' },
    { id: 'file-text', name: 'Document' },
    { id: 'message-square', name: 'Chat' },
    { id: 'lightbulb', name: 'Idea' },
    { id: 'check-circle', name: 'Checkmark' },
    { id: 'alert-circle', name: 'Alert' }
  ];

  // Color options for icon background
  const colorOptions = [
    { id: 'bg-sky-500', name: 'Blue' },
    { id: 'bg-emerald-500', name: 'Green' },
    { id: 'bg-purple-500', name: 'Purple' },
    { id: 'bg-rose-500', name: 'Rose' },
    { id: 'bg-amber-500', name: 'Amber' },
    { id: 'bg-indigo-500', name: 'Indigo' },
    { id: 'bg-pink-500', name: 'Pink' },
    { id: 'bg-teal-500', name: 'Teal' }
  ];

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', damping: 25, stiffness: 500 } }
  };

  // Helper to render the icon preview
  const renderIconPreview = () => {
    const icon = icons.find(i => i.id === selectedIcon);
    const color = colorOptions.find(c => c.id === iconBgColor);

    return (
      <div className="flex items-center space-x-3">
        <div className={`p-3 rounded-full ${iconBgColor} text-white`}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {selectedIcon === 'bookmark' && <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>}
            {selectedIcon === 'star' && <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>}
            {selectedIcon === 'heart' && <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>}
            {selectedIcon === 'file-text' && <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></>}
            {selectedIcon === 'message-square' && <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>}
            {selectedIcon === 'lightbulb' && <path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>}
            {selectedIcon === 'check-circle' && <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></>}
            {selectedIcon === 'alert-circle' && <><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></>}
          </svg>
        </div>
        <div className="text-zinc-100 text-base truncate max-w-xs">{name || 'Bookmark Name'}</div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          onClick={onClose}
        >
          <motion.div
            className="bg-zinc-900 rounded-xl w-full max-w-5xl overflow-hidden flex flex-col"
            variants={modalVariants}
            onClick={e => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className=" p-8 flex justify-between items-center bxorder-b border-zinc-800">
                <h2 className="text-xl   text-zinc-50">Save Favorite</h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-zinc-400 hover:text-zinc-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>

              </div>

              {/* Content */}
              <div className="p-8 pt-0 flex-1 overflow-y-auto">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Preview Section - Moved to the left side */}
                  <div className="md:w-1/3">
                    <div className="bg-black/20 p-4 rounded-lg h-full flex flex-col">
                      <h3 className="text-sm font-medium text-zinc-400 mb-3">Preview</h3>
                      <div className="grid grid-cols-1 gap-2 overflow-y-auto">
                        {/* Current bookmark being created */}
                        <div className={`
                        ${displayType === 'inline' ? 'bg-zinc-800/50' : 'bg-zinc-700/50'} 
                        p-3 rounded-lg`}>
                          <div className="flex items-center space-x-3">
                           
                          {displayType != 'inline' ?
                            <div className={`p-2 rounded-full ${iconBgColor} text-white`}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {selectedIcon === 'bookmark' && <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>}
                                {selectedIcon === 'star' && <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>}
                                {selectedIcon === 'heart' && <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>}
                                {selectedIcon === 'file-text' && <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></>}
                                {selectedIcon === 'message-square' && <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>}
                                {selectedIcon === 'lightbulb' && <path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8A6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>}
                                {selectedIcon === 'check-circle' && <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></>}
                                {selectedIcon === 'alert-circle' && <><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></>}
                              </svg>
                            </div>
  : null }

                            <div className="text-zinc-100 text-sm truncate max-w-full whitespace-normal">
                              {displayType === 'inline' ? (
                                <div className="text-base leading-snug">
                                  Select any of Coco's messages to go deeper with key insights that help to focus and refine your message{' '}
                                  <span
                                    className="relative group bg-sky-500 text-white px-1 py-0.5 rounded"
                                  >
                                    {USER_HIGHLIGHTED_TEXT}
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap bg-zinc-950 shadow-lg text-white px-4 py-4 text-sm rounded-lg  opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                      {name}
                                      {/* Carrot */}
                                      <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-zinc-950"></div>
                                    </div>
                                  </span>
                                  {' '}to connect in your customer's language and mindset
                                </div>
                              ) : (
                                name || 'Bookmark Name'
                              )}
                            </div>
                          </div>
                        </div>


                        {displayType != 'inline' ?
                          <>
                            <div className="bg-zinc-800/70 p-3 rounded-lg opacity-50">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-full bg-purple-500 text-white">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                  </svg>
                                </div>
                                <div className="text-zinc-400 text-sm truncate max-w-[160px]">Meeting Notes</div>
                              </div>
                            </div>

                            <div className="bg-zinc-800/70 p-3 rounded-lg opacity-50">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-full bg-emerald-500 text-white">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                    <polyline points="10 9 9 9 8 9"></polyline>
                                  </svg>
                                </div>
                                <div className="text-zinc-400 text-sm truncate max-w-[160px]">Documentation</div>
                              </div>
                            </div>

                            <div className="bg-zinc-800/70 p-3 rounded-lg opacity-50">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-full bg-amber-500 text-white">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                  </svg>
                                </div>
                                <div className="text-zinc-400 text-sm truncate max-w-[160px]">Important Task</div>
                              </div>
                            </div>
                          </>
                          :
                          null
                        }
                      </div>
                    </div>
                  </div>

                  {/* Form Fields - Right side */}
                  <div className="md:w-2/3 ml-2 space-y-6">
                    {/* Name Field */}
                    <div>
                      <label htmlFor="bookmark-name" className="block text-base font-medium text-zinc-100">
                        Name
                      </label>
                      <input
                        type="text"
                        id="bookmark-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 block w-full bg-zinc-800/50 border border-zinc-700 rounded-lg p-2.5 text-zinc-100 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        required
                      />
                    </div>

                    {/* Content Textarea */}
                    <div>
                      <label htmlFor="bookmark-content" className="block text-base font-medium text-zinc-100">
                        Message
                      </label>
                      {displayType === 'appBar' ? (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <textarea
                            id="bookmark-content"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            rows={4}
                            className="mt-1 block w-full bg-zinc-800/50 border border-zinc-700 rounded-lg p-2.5 text-zinc-100 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                            required
                          />
                        </motion.div>
                      ) : (
                        // displayType === 'inline'
                        <motion.div 
                          className="space-y-2 mt-1"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <motion.textarea
                            id="bookmark-content-start"
                            value={contentStart}
                            onChange={(e) => setContentStart(e.target.value)}
                            rows={3}
                            className="block w-full bg-zinc-800/50 border border-zinc-700 rounded-lg p-2.5 text-zinc-100 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                            placeholder="Content before highlighted text..."
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          />
                          <motion.div 
                            className="relative group bg-black/50 text-white px-1 py-0.5 rounded"
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.2 }}
                          >
                            {USER_HIGHLIGHTED_TEXT}
                          </motion.div>
                          <motion.textarea
                            id="bookmark-content-end"
                            value={contentEnd}
                            onChange={(e) => setContentEnd(e.target.value)}
                            rows={3}
                            className="block w-full bg-zinc-800/50 border border-zinc-700 rounded-lg p-2.5 text-zinc-100 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                            placeholder="Content after highlighted text..."
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.3 }}
                          />
                        </motion.div>
                      )}
                    </div>

                    {/* Display As Toggle */}
                    <div>
                      <label className="block text-base font-medium text-zinc-100 mb-2">
                        Display As
                      </label>
                      <div className="flex space-x-1 rounded-lg bg-zinc-800 p-1">
                        <button
                          type="button"
                          onClick={() => handleDisplayTypeChange('appBar')}
                          className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors
                            ${displayType === 'appBar'
                              ? 'bg-sky-500 text-white'
                              : 'text-zinc-300 hover:bg-zinc-700'
                            }`}
                        >
                          App Bar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDisplayTypeChange('inline')}
                          className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors
                            ${displayType === 'inline'
                              ? 'bg-sky-500 text-white'
                              : 'text-zinc-300 hover:bg-zinc-700'
                            }`}
                        >
                          Inline
                        </button>
                      </div>
                    </div>

                    {/* Availability Section */}
                    <div>
                      <div className="flex items-start justify-between"> {/* Main row for Sharing and Favorite columns */}
                        {/* Sharing Column (Left Side) */}
                        <div>
                          <label className="block text-base font-medium text-zinc-100 mb-2">
                            Sharing
                          </label>
                          <div className="flex flex-wrap gap-3 text-sm">
                            <button
                              type="button"
                              onClick={() => handleAvailabilityChange('private')}
                              className={`px-4 py-4 rounded-lg flex items-center space-x-2 ${availability.private
                                ? 'bg-sky-500 text-white'
                                : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                                }`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                              </svg>
                              <span>private</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleAvailabilityChange('company')}
                              className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${availability.company
                                ? 'bg-sky-500 text-white'
                                : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                                }`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                              </svg>
                              <span>company</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleAvailabilityChange('community')}
                              className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${availability.community
                                ? 'bg-sky-500 text-white'
                                : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                                }`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                              <span>community</span>
                            </button>
                          </div>
                        </div>

                        {/* Favorite Column (Right Side) */}
                        <div className="flex flex-col items-start"> {/* items-start for left alignment of children */}
                          <label className="block text-base font-medium text-zinc-100 mb-2">
                            Favorite
                          </label>
                          <button
                            type="button"
                            onClick={() => setFavorite(!favorite)}
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 w-28 justify-center ${favorite
                              ? 'bg-sky-500 text-white'
                              : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                              }`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                            </svg>
                            <span>favorite</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Icon Selection */}
                    <div>
                      <label className="block text-base font-medium text-zinc-100 mb-2">
                        Icon
                      </label>
                      <div className="grid grid-cols-8 gap-2">
                        {icons.map((icon) => (
                          <button
                            key={icon.id}
                            type="button"
                            onClick={() => setSelectedIcon(icon.id)}
                            className={`p-2 rounded-lg flex py-5 flex-col items-center ${selectedIcon === icon.id
                              ? 'bg-zinc-700 ring-2 ring-sky-500'
                              : 'bg-zinc-800 hover:bg-zinc-700'
                              }`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-200">
                              {icon.id === 'bookmark' && <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>}
                              {icon.id === 'star' && <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>}
                              {icon.id === 'heart' && <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>}
                              {icon.id === 'file-text' && <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></>}
                              {icon.id === 'message-square' && <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>}
                              {icon.id === 'lightbulb' && <path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8A6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>}
                              {icon.id === 'check-circle' && <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></>}
                              {icon.id === 'alert-circle' && <><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></>}
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Icon Background Color - Updated to use rounded-lg instead of rounded-full */}
                    <div>
                      <label className="block text-base font-medium text-zinc-100 mb-2">
                        Background
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {colorOptions.map((color) => (
                          <button
                            key={color.id}
                            type="button"
                            onClick={() => setIconBgColor(color.id)}
                            className={`w-8 h-8 rounded-lg ${color.id} ${iconBgColor === color.id
                              ? 'ring-2 ring-offset-2 ring-white'
                              : ''
                              }`}
                            title={color.name}
                          />
                        ))}
                      </div>
                    </div>


                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className=" p-4 boxrder-t border-zinc-800 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-sky-500 hover:bg-sky-400 text-white rounded-lg"
                >
                  Save
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Bookmark;